/*
Copyright 2022 Fondazione Bruno Kessler
Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
except in compliance with the Licence.

You may obtain a copy of the Licence at:
http://ec.europa.eu/idabc/eupl.html

Unless required by applicable law or agreed to in writing, software distributed under 
the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF 
ANY KIND, either express or implied. See the Licence for the specific language 
governing permissions and limitations under the Licence.
*/

import { ColDef, GridOptions } from "@ag-grid-community/core";
import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { CommonState } from "@app/store/common/common.state";
import { TP_ALL_SECTIONS } from "@doctor-panel-modules/patient-treatment-plan/models/tp-sections-info";
import { ModalController } from "@ionic/angular";
import { DoctorPanelActions } from "@modules/doctor-panel/store/doctor-panel.actions";
import { DoctorPanelState } from "@modules/doctor-panel/store/doctor-panel.state";
import { hasActionsExecuting } from "@ngxs-labs/actions-executing";
import { Select, Store } from "@ngxs/store";
import { ContestoPaziente, Icd9Patient } from "@trec/api";
import { C3TmplCellComponent, C3TmplCellOptions } from "@trec/components/table";
import { Observable } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { EditIcd9DialogComponent } from "../../edit/edit-icd9-dialog/edit-icd9-dialog.component";

@Component({
  selector: "c3-config-icd9",
  templateUrl: "./config-icd9.component.html",
  styleUrls: ["./config-icd9.component.scss"],
  standalone: false,
})
export class ConfigIcd9Component implements OnInit, AfterViewInit {
  @Select(hasActionsExecuting([DoctorPanelActions.FetchIcd9List]))
  loading$: Observable<boolean>;

  @Select(DoctorPanelState.icd9Patient)
  icd9PatientList$: Observable<Icd9Patient[]>;

  @Select(CommonState.currentPatientAllContexts)
  currentPatientAllContexts$: Observable<ContestoPaziente[]>;

  // Observable che trasforma currentPatientAllContexts in una mappa ID -> label e current
  contextInfo$: Observable<{ [key: string]: { label: string; current?: boolean } }>;

  // Proprietà per memorizzare il valore attuale di contextInfo
  contextInfo: { [key: string]: { label: string; current?: boolean } } = {};

  TP_SECTIONS_INFO = TP_ALL_SECTIONS;

  readonly customAgGridOptions: GridOptions = {
    domLayout: "autoHeight",
    defaultColDef: {
      sortable: false,
      filter: false,
    },
    pagination: false,
    tooltipShowDelay: 500,
    enableBrowserTooltips: true,
  };
  columnDefs: ColDef[];

  @ViewChild("actionsCellTmpl")
  actionsCellTmpl!: TemplateRef<unknown>;

  constructor(
    private store: Store,
    private modalCtrl: ModalController,
  ) {}

  ngOnInit(): void {
    this.contextInfo$ = this.currentPatientAllContexts$.pipe(
      map((contexts: ContestoPaziente[]) => {
        const contextMap: { [key: string]: { label: string; current?: boolean } } = {};
        contexts.forEach((context) => {
          if (context.id) {
            contextMap[context.id] = {
              label: context.descrizione || context.id,
              current: context.current || false,
            };
          }
        });
        return contextMap;
      }),
      shareReplay(1),
    );

    // Memorizza il valore attuale di contextInfo
    this.contextInfo$.subscribe((contextInfo) => {
      this.contextInfo = contextInfo;
    });
  }

  ngAfterViewInit() {
    this.contextInfo$.subscribe((contextInfo) => {
      this.columnDefs = [
        {
          headerName: `Contesto`,
          colId: "context",
          flex: 1,
          minWidth: 120,
          valueGetter: (params) =>
            params.data?.context && contextInfo[params.data.context]
              ? contextInfo[params.data.context].label
              : (params.data?.context ?? "n/d"),
        },
        {
          headerName: `Codice ICD-9`,
          colId: "icd9Code",
          flex: 1,
          minWidth: 70,
          valueGetter: (params) => params.data?.icd9Code ?? "n/d",
        },
        {
          headerName: `Descrizione Diagnosi`,
          colId: "icd9Description",
          flex: 3,
          minWidth: 200,
          valueGetter: (params) => params.data?.icd9Description ?? "n/d",
          tooltipValueGetter: (params) => {
            // Mostra la descrizione completa nel tooltip
            return params.data?.icd9Description ?? "Nessuna descrizione disponibile";
          },
        },
        {
          headerName: `Azioni`,
          colId: "actions",
          width: 90,
          maxWidth: 90,
          suppressSizeToFit: true,
          cellRenderer: C3TmplCellComponent,
          cellRendererParams: { template: this.actionsCellTmpl } as C3TmplCellOptions,
        },
      ];

      this.getList();
    });
  }

  async openEditDialog(icd9Patient: Icd9Patient) {
    const modal = await this.modalCtrl.create({
      component: EditIcd9DialogComponent,
      componentProps: {
        icd9Code: icd9Patient.icd9Code,
        context: icd9Patient.context,
        icd9Description: icd9Patient.icd9Description,
      },
      cssClass: "c3-modal-width-sm c3-modal-height-md",
    });
    await modal.present();
  }

  /*async openDeleteDialog(icd9: Icd9Patient) {
    const alert = await this.alertCtrl.create({
      header: "Cancellazione diagnosi",
      message: `Vuoi cancellare questa diagnosi con codice ICD-9?`,
      buttons: [
        { text: "No", role: "cancel" },
        {
          text: "Sì",
          handler: () => {
            let patientId = this.store.selectSnapshot(CommonState.patientId)!;
            this.store
              .dispatch(new DoctorPanelActions.DeleteIcd9(icd9.context, patientId))
              .subscribe(
                () => {
                  this.c3Snackbar.showSuccess({
                    message: `Codice ICD-9 cancellato correttamente`,
                  });
                },
                () => {}
              );
          },
        },
      ],
    });

    await alert.present();
  }*/

  getList() {
    let patientId = this.store.selectSnapshot(CommonState.patientId)!;
    this.store.dispatch(new DoctorPanelActions.FetchIcd9Patient(patientId));
  }

  removeQuotes(value: string): string {
    return value ? value.replace(/"/g, "") : "";
  }
}
